
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    tipoCaso: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const caseTypes = [
    'Isenção de Imposto de Renda',
    'Pensão por Morte',
    'Aposentadoria',
    'BPC/LOAS',
    'Revisão de Benefícios',
    'Planejamento Previdenciário',
    'Outro'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();

      // Mapping original field names to recognizable names for the email
      formDataToSend.append("Nome", formData.nome);
      formDataToSend.append("Email", formData.email);
      formDataToSend.append("Telefone", formData.telefone);
      formDataToSend.append("Tipo de Caso", formData.tipoCaso);
      formDataToSend.append("Mensagem", formData.mensagem);

      // Web3Forms Configuration
      formDataToSend.append("access_key", "64212863-bd11-4fa3-b199-4cc13475309f");
      formDataToSend.append("subject", "Nova mensagem do site - Albiana Guerreiro Advocacia");
      formDataToSend.append("from_name", "Website Albiana Guerreiro");
      formDataToSend.append("replyto", formData.email);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          tipoCaso: '',
          mensagem: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#214134] mb-6">
            Entre em Contato
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Estamos prontos para ajudar você. Entre em contato conosco e agende sua consulta
            para discutir seu caso específico.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Informações de Contato */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-[#214134] mb-6">
                Informações de Contato
              </h3>

              <div className="space-y-6">
                {/* WhatsApp */}
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#214134] mb-1">WhatsApp</h4>
                    <p className="text-gray-600 mb-2">(44) 98801-9071</p>
                    <a
                      href="https://wa.me/5544988019071?text=Olá! Gostaria de agendar uma consulta."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#cc9b3f] hover:underline cursor-pointer"
                    >
                      Enviar mensagem
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#214134] rounded-full flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-[#cc9b3f]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#214134] mb-1">Email</h4>
                    <p className="text-gray-600 mb-2">contato@albianaguerreiro.com.br</p>
                    <a
                      href="mailto:contato@albianaguerreiro.com.br"
                      className="text-[#cc9b3f] hover:underline cursor-pointer"
                    >
                      Enviar email
                    </a>
                  </div>
                </div>

                {/* Endereço */}
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#214134] rounded-full flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl text-[#cc9b3f]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#214134] mb-1">Endereço</h4>
                    <p className="text-gray-600 mb-2">
                      Av. Mandacaru, 886 - Sala 1<br />
                      Maringá/PR
                    </p>
                    <a
                      href="https://maps.google.com/?q=Av.+Mandacaru,+886,+Maringá,+PR"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#cc9b3f] hover:underline cursor-pointer"
                    >
                      Ver no Google Maps
                    </a>
                  </div>
                </div>

                {/* Horário */}
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#214134] rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-xl text-[#cc9b3f]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#214134] mb-1">Horário de Atendimento</h4>
                    <p className="text-gray-600">
                      Segunda a Sexta<br />
                      09:00 às 12:00<br />
                      13:30 às 17:00
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Atendimento */}
            <div className="bg-[#214134] p-6 rounded-xl text-white">
              <h4 className="text-xl font-bold mb-4 text-[#cc9b3f]">Modalidades de Atendimento</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <i className="ri-building-line text-[#cc9b3f]"></i>
                  <span>Presencial em Maringá/PR</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-global-line text-[#cc9b3f]"></i>
                  <span>Online para todo o Brasil</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-user-heart-line text-[#cc9b3f]"></i>
                  <span>Recomenda-se acompanhante para idosos no atendimento online</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Formulário */}
          <div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-[#214134] mb-6">
                Envie sua Mensagem
              </h3>

              <form id="contato-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                {/* Anti-spam honeypot */}
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />

                {/* Nome */}
                <div>
                  <label htmlFor="nome" className="block text-sm font-semibold text-[#214134] mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#cc9b3f] focus:border-transparent text-sm"
                    placeholder="Seu nome completo"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#214134] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#cc9b3f] focus:border-transparent text-sm"
                    placeholder="seu@email.com"
                  />
                </div>

                {/* Telefone */}
                <div>
                  <label htmlFor="telefone" className="block text-sm font-semibold text-[#214134] mb-2">
                    Telefone/WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#cc9b3f] focus:border-transparent text-sm"
                    placeholder="(44) 99999-9999"
                  />
                </div>

                {/* Tipo de Caso */}
                <div>
                  <label htmlFor="tipoCaso" className="block text-sm font-semibold text-[#214134] mb-2">
                    Tipo de Caso *
                  </label>
                  <div className="relative">
                    <select
                      id="tipoCaso"
                      name="tipoCaso"
                      value={formData.tipoCaso}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#cc9b3f] focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">Selecione o tipo de caso</option>
                      {caseTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>

                {/* Mensagem */}
                <div>
                  <label htmlFor="mensagem" className="block text-sm font-semibold text-[#214134] mb-2">
                    Mensagem *
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#cc9b3f] focus:border-transparent text-sm resize-vertical"
                    placeholder="Descreva brevemente seu caso ou dúvida..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    Máximo 500 caracteres ({formData.mensagem.length}/500)
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Enviar Mensagem
                    </>
                  )}
                </Button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-circle-line text-green-600"></i>
                      <span>Mensagem enviada com sucesso! Entraremos em contato em breve.</span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <i className="ri-error-warning-line text-red-600"></i>
                      <span>Erro ao enviar mensagem. Tente novamente ou entre em contato via WhatsApp.</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Google Maps */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-[#214134] text-center mb-8">
            Nossa Localização
          </h3>
          <div className="bg-gray-200 rounded-xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3656.123456789!2d-51.9356!3d-23.4205!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDI1JzEzLjgiUyA1McKwNTYnMDguMiJX!5e0!3m2!1spt-BR!2sbr!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização do Escritório - Av. Mandacaru, 886, Maringá/PR"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
